/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import { Box } from '@material-ui/core';
import { useAlertStyles, useErrorOutlineStyles } from './style';

const ErrorOutlineStyled = ({ severity }) => {
  const classes = useErrorOutlineStyles({ severity });
  return React.createElement(ErrorOutline, { classes: classes,} );
};
const ExpandMoreIconStyled = ({ severity }) => {
  const classes = useErrorOutlineStyles({ severity });
  return React.createElement(ExpandMoreIcon, { classes: classes,} );
};





















export const capitalize = (s) => {
  return s.charAt(0).toLocaleUpperCase('en-US') + s.slice(1);
};

/**
 * Show a user friendly error message to a user similar to
 * ErrorPanel except that the warning panel only shows the warning message to
 * the user.
 *
 * @param severity - Ability to change the severity of the alert. Default value
 *        "warning"
 * @param title - A title for the warning. If not supplied, "Warning" will be
 *        used.
 * @param message - Optional more detailed user-friendly message elaborating on
 *        the cause of the error.
 * @param children - Objects to provide context, such as a stack trace or detailed
 *        error reporting. Will be available inside an unfolded accordion.
 */
export function Alert(props) {
  const {
    severity = 'warning',
    title,
    message,
    children,
    defaultExpanded,
    button,
    icon,
    image,
  } = props;
  const classes = useAlertStyles({ severity });

  // If no severity or title provided, the heading will read simply "Warning"
  const subTitle = capitalize(severity) + (title ? `: ${title}` : '');

  return (
    React.createElement(Accordion, {
      defaultExpanded: defaultExpanded ?? false,
      className: classes.panel,
      role: "alert",}
    
      , React.createElement(AccordionSummary, {
        expandIcon: 
          message || (children && React.createElement(ExpandMoreIconStyled, { severity: severity,} ))
        ,
        className: classes.summary,}
      
        , icon ? icon : React.createElement(ErrorOutlineStyled, { severity: severity,} )
        , React.createElement(Typography, { className: classes.summaryText, variant: "body2",}
          , subTitle
        )
      )
      , (message || children) && (
        React.createElement(AccordionDetails, null
          , React.createElement(Grid, { container: true, className: classes.innerContainer,}
            , image
            , message && (
              React.createElement(Typography, { className: classes.text, variant: "subtitle1",}
                , message
              )
            )
            , children && (
              React.createElement(Typography, { className: classes.text, variant: "subtitle1",}
                , children
              )
            )
            , button && (
              React.createElement(Box, { display: "flex", justifyContent: "end",}
                , button
              )
            )
          )
        )
      )
    )
  );
}
